import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { httpClient } from "../../../services/httpClient";
import { showToast } from "../../../utils/showToast";

const CLAVE_ACT = "campus_ponderacion_actividades";
const CLAVE_EV  = "campus_ponderacion_evaluacion";

export default function ConfiguracionCampus() {
  const [pondAct, setPondAct] = useState("70");
  const [pondEv,  setPondEv]  = useState("30");
  const [saving,  setSaving]  = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    httpClient.get("/api/configuracion")
      .then((data) => {
        const map = Object.fromEntries(data.map((r) => [r.clave, r.valor]));
        if (map[CLAVE_ACT]) setPondAct(map[CLAVE_ACT]);
        if (map[CLAVE_EV])  setPondEv(map[CLAVE_EV]);
      })
      .catch(() => showToast({ type: "danger", message: "No se pudo cargar la configuración." }))
      .finally(() => setLoading(false));
  }, []);

  const guardar = async () => {
    const act = parseInt(pondAct, 10);
    const ev  = parseInt(pondEv,  10);
    if (isNaN(act) || isNaN(ev) || act < 0 || ev < 0 || act + ev !== 100) {
      showToast({ type: "warning", message: "Los porcentajes deben sumar exactamente 100." });
      return;
    }
    setSaving(true);
    try {
      await httpClient.put(`/api/configuracion/${CLAVE_ACT}`, { valor: String(act) });
      await httpClient.put(`/api/configuracion/${CLAVE_EV}`,  { valor: String(ev) });
      showToast({ type: "success", message: "Configuración guardada correctamente." });
    } catch (err) {
      showToast({ type: "danger", message: err.message || "Error al guardar." });
    } finally {
      setSaving(false);
    }
  };

  const total = parseInt(pondAct || 0) + parseInt(pondEv || 0);
  const totalOk = total === 100;

  return (
    <section className="management-page management-theme-teal">
      <div className="container-fluid">
        <div className="management-card management-header mb-4">
          <div>
            <Link to="/dashboard/configuracion" className="btn btn-link btn-sm p-0 text-muted text-decoration-none mb-2">
              <i className="bi bi-arrow-left me-1" />Configuración
            </Link>
            <h5 className="mb-1">
              <i className="bi bi-mortarboard me-2 text-primary" />Campus Ademy
            </h5>
            <p className="text-muted mb-0">Parámetros del portal estudiantil y docente.</p>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-5"><span className="spinner-border text-primary" /></div>
        ) : (
          <div className="row g-4">

            {/* Ponderación de notas */}
            <div className="col-12 col-lg-6">
              <div className="management-card p-4">
                <h6 className="fw-bold mb-1">
                  <i className="bi bi-calculator me-2 text-primary" />Ponderación de notas
                </h6>
                <p className="text-muted small mb-4">
                  Define qué porcentaje de la nota final corresponde a las actividades parciales
                  y cuánto al examen final. Aplica a todas las nuevas secciones creadas automáticamente.
                </p>

                <div className="row g-3 mb-3">
                  <div className="col-6">
                    <label className="form-label small fw-semibold">% Actividades parciales</label>
                    <div className="input-group">
                      <input
                        type="number" className="form-control text-center fw-bold"
                        min={0} max={100} value={pondAct}
                        onChange={(e) => setPondAct(e.target.value)}
                        style={{ fontSize: 20 }}
                      />
                      <span className="input-group-text">%</span>
                    </div>
                    <div className="form-text">Tareas, quizzes, trabajos</div>
                  </div>
                  <div className="col-6">
                    <label className="form-label small fw-semibold">% Examen final</label>
                    <div className="input-group">
                      <input
                        type="number" className="form-control text-center fw-bold"
                        min={0} max={100} value={pondEv}
                        onChange={(e) => setPondEv(e.target.value)}
                        style={{ fontSize: 20 }}
                      />
                      <span className="input-group-text">%</span>
                    </div>
                    <div className="form-text">Evaluación final de la materia</div>
                  </div>
                </div>

                {/* Barra visual */}
                <div className="mb-3">
                  <div className="d-flex mb-1 small">
                    <span className="text-primary fw-semibold">{pondAct}% Actividades</span>
                    <span className="ms-auto text-secondary fw-semibold">{pondEv}% Examen</span>
                  </div>
                  <div className="progress" style={{ height: 12, borderRadius: 6 }}>
                    <div
                      className="progress-bar bg-primary"
                      style={{ width: `${Math.min(100, parseInt(pondAct) || 0)}%`, borderRadius: '6px 0 0 6px' }}
                    />
                    <div
                      className="progress-bar bg-secondary"
                      style={{ width: `${Math.min(100, parseInt(pondEv) || 0)}%`, borderRadius: '0 6px 6px 0' }}
                    />
                  </div>
                  <div className={`small mt-1 fw-semibold ${totalOk ? 'text-success' : 'text-danger'}`}>
                    Total: {total}% {totalOk ? '✓' : '— debe ser exactamente 100%'}
                  </div>
                </div>

                <div className="alert alert-warning small py-2 mb-3">
                  <i className="bi bi-exclamation-triangle me-1" />
                  Este cambio afecta únicamente las <strong>nuevas secciones</strong> que se creen a partir
                  de ahora. Las secciones existentes conservan su ponderación actual.
                </div>

                <button
                  className="btn management-btn-primary"
                  onClick={guardar}
                  disabled={saving || !totalOk}
                >
                  {saving
                    ? <><span className="spinner-border spinner-border-sm me-2" />Guardando…</>
                    : <><i className="bi bi-floppy me-2" />Guardar configuración</>
                  }
                </button>
              </div>
            </div>

            {/* Umbral de aprobación (informativo por ahora) */}
            <div className="col-12 col-lg-6">
              <div className="management-card p-4">
                <h6 className="fw-bold mb-1">
                  <i className="bi bi-check-circle me-2 text-success" />Umbral de aprobación
                </h6>
                <p className="text-muted small mb-3">
                  Nota mínima para aprobar una materia o curso.
                </p>
                <div className="d-flex align-items-center gap-3">
                  <div
                    className="rounded-3 d-flex align-items-center justify-content-center fw-bold text-success border border-success"
                    style={{ width: 64, height: 64, fontSize: 28 }}
                  >
                    7
                  </div>
                  <div>
                    <div className="fw-semibold">Nota mínima: 7 / 10</div>
                    <div className="text-muted small">Configurable en una próxima versión.</div>
                  </div>
                </div>
              </div>
            </div>

          </div>
        )}
      </div>
    </section>
  );
}
